import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { assetAuditsApi, assetLocationsApi } from '@/services/api/endpoints/assets';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import type { AssetAudit, StartAuditInput } from '@/types/asset';
import { formatDateTime } from '@/utils/format';
import { AuditStatusBadge } from './shared';

/**
 * Asset counts.
 *
 * <p>Every tally on this list is the server's: expected is the snapshot taken
 * when the count was opened, and found / missing / unchecked / misplaced are
 * counted from its lines. Nothing is added up in the browser.
 *
 * <p>Opening a count snapshots every non-disposed asset in scope — one
 * location, or the whole school. Lines then stay unchecked until somebody
 * checks them; an unchecked line is never presumed found.
 */
export function AssetAuditsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const counts = can(PERMISSIONS.ASSET_AUDIT);

  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const [opening, setOpening] = useState(false);
  const [locationId, setLocationId] = useState('');
  const [notes, setNotes] = useState('');

  const params = { page, size };

  const audits = useQuery({
    queryKey: queryKeys.assetAudits.list(params),
    queryFn: () => assetAuditsApi.list(params),
  });

  const locations = useQuery({
    queryKey: queryKeys.assetReference.locations(undefined),
    queryFn: () => assetLocationsApi.list(),
    enabled: opening,
  });

  const activeLocations = (locations.data ?? []).filter((row) => row.status === 'ACTIVE');

  function openForm() {
    setLocationId('');
    setNotes('');
    setOpening(true);
  }

  const start = useMutation({
    mutationFn: () => {
      const payload: StartAuditInput = {
        locationId: locationId === '' ? undefined : locationId,
        notes: notes.trim() === '' ? undefined : notes.trim(),
      };
      return assetAuditsApi.start(payload);
    },
    onSuccess: (audit) => {
      toast.success(`Count ${audit.auditNumber} opened with ${audit.expected} expected`);
      setOpening(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.assetAudits.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.assets.dashboard });
      navigate(ROUTES.assetAuditDetail(audit.id));
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<AssetAudit>[] = [
    {
      key: 'count',
      header: 'Count',
      render: (row) => (
        <span>
          <Link
            to={ROUTES.assetAuditDetail(row.id)}
            className="block font-mono text-sm font-semibold text-ink underline-offset-2 hover:text-accent hover:underline"
          >
            {row.auditNumber}
          </Link>
          <span className="block text-[11px] text-subtle">
            {row.locationName ?? 'Whole school'}
          </span>
        </span>
      ),
    },
    {
      key: 'started',
      header: 'Opened',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{formatDateTime(row.startedAt)}</span>
          {row.closedAt !== undefined && (
            <span className="block text-[11px] text-subtle">
              closed {formatDateTime(row.closedAt)}
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'expected',
      header: 'Expected',
      className: 'text-right',
      render: (row) => <span className="tabular-nums">{row.expected}</span>,
    },
    {
      key: 'found',
      header: 'Found',
      className: 'text-right',
      render: (row) => <span className="tabular-nums">{row.found}</span>,
    },
    {
      key: 'missing',
      header: 'Missing',
      className: 'text-right',
      render: (row) => (
        <span
          className={`tabular-nums ${row.missing > 0 ? 'font-semibold text-[color:var(--sms-danger)]' : ''}`}
        >
          {row.missing}
        </span>
      ),
    },
    {
      key: 'unchecked',
      header: 'Unchecked',
      className: 'text-right',
      render: (row) => <span className="tabular-nums">{row.unchecked}</span>,
    },
    {
      key: 'misplaced',
      header: 'Misplaced',
      className: 'text-right',
      render: (row) => <span className="tabular-nums">{row.misplaced}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <AuditStatusBadge status={row.status} />,
    },
  ];

  return (
    <>
      <ListPage
        title="Asset counts"
        description="Physical counts of the register. Discrepancies are reported, never applied on their own."
        icon={<ClipboardCheck className="h-5 w-5" aria-hidden="true" />}
        action={counts ? <Button onClick={openForm}>Open a count</Button> : undefined}
        pagination={audits.data?.pagination}
        onPageChange={setPage}
        onSizeChange={(next) => {
          setSize(next);
          setPage(0);
        }}
      >
        <DataTable
          columns={columns}
          rows={audits.data?.data}
          rowKey={(row) => row.id}
          loading={audits.isPending}
          error={audits.error}
          onRetry={() => void audits.refetch()}
          emptyTitle="No counts yet"
          emptyDescription={
            counts
              ? 'Open a count for one location or the whole school to check the register against the shelves.'
              : 'Nobody has counted the register yet.'
          }
        />
      </ListPage>

      <Modal
        open={opening}
        onClose={() => setOpening(false)}
        title="Open a count"
        description="Opening snapshots every non-disposed asset in scope. Each line then stays unchecked until somebody checks it."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpening(false)}>
              Cancel
            </Button>
            <Button onClick={() => start.mutate()} loading={start.isPending}>
              Open the count
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <SelectField
            label="What to count"
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
            disabled={locations.isPending}
            hint={
              locations.isError
                ? 'Locations could not be loaded; the whole school can still be counted.'
                : 'One location, or every asset the school has.'
            }
          >
            <option value="">Whole school</option>
            {activeLocations.map((row) => (
              <option key={row.id} value={row.id}>
                {row.campusName === undefined ? row.name : `${row.name} — ${row.campusName}`}
              </option>
            ))}
          </SelectField>
          <TextField
            label="Notes"
            value={notes}
            maxLength={1000}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
      </Modal>
    </>
  );
}
